const simplify = require('simplify-path');

// Converts 2 coordinates into a distnance in m's
export const distanceFromP1toP2 = (p1, p2) => {
  // Description of math: https://www.movable-type.co.uk/scripts/latlong.html

  const R = 6371000; // Radius of earth in m
  const phi1 = p1[0];
  const phi2 = p2[0];
  const deltaPhi = phi2 - phi1;
  const deltaLambda = p2[1] - p1[1];
  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.abs(R * c);
};

// Creates a triangular matrix that hold the distance from each point to all other points eg
//
//  d00
//  d10 d11
//  d20 d21 d22
export const createDistanceMatrix = points => {
  const triangleArr = [];
  let i = 0;
  let j = 0;
  while (j < points.length) {
    triangleArr[j] = [];
    for (i = 0; i < j + 1; i++) {
      triangleArr[j][i] = distanceFromP1toP2(points[i], points[j]);
    }
    j++;
  }
  return triangleArr;
};

export const addToDistanceMatrix = (points, distanceMatrix) => {
  distanceMatrix[distanceMatrix.length] = [];
  for (let i = 0; i < distanceMatrix.length; i++) {
    distanceMatrix[distanceMatrix.length - 1][i] = distanceFromP1toP2(points[i], points[distanceMatrix.length - 1]);
  }
};

// Takes in a precomputed distance array and performs steps. It finds the max distance for each step one by one to every point and then returns
// the maximum distance to any specified point.
export const maximiseNPointDistance = (n, distMatrix, points) => {
  const distanceToPoints = Array(...Array(points.length)).map(Number.prototype.valueOf, 0);
  let steps = 0;

  while (steps < n - 1) {
    const prevDistanceToPoints = distanceToPoints.slice();
    // i represents the element in the final array
    for (let i = steps + 1; i < points.length; i++) {
      // itterating through all previous elements to find the max one
      let maxDist = 0;
      for (let j = 0; j < i; j++) {
        const tempDistFromJ = prevDistanceToPoints[j] + distMatrix[i][j];
        if (tempDistFromJ > maxDist) {
          maxDist = tempDistFromJ;
        }
      }
      distanceToPoints[i] = maxDist;
    }
    steps++;
  }

  return Math.max(...distanceToPoints);
};

// Gets the rectangular bounds in lat and long and returns the smaller one
export const smallestRectLen = points => {
  let boundaryX = 0;
  let boundaryY = 0;

  points.forEach(element => {
    const xDist = Math.abs(points[0][0] - element[0]);
    const yDist = Math.abs(points[0][0] - element[0]);

    if (xDist > boundaryX) {
      boundaryX = xDist;
    }

    if (yDist > boundaryY) {
      boundaryY = yDist;
    }
  });

  return Math.min(boundaryX, boundaryY);
};

export const applyXcStat = (point, context) => {
  // If the point history is undefined, define all data storage neccassary for the stat in context
  if (context.simplifiedPointHistory === undefined) {
    context.simplifiedPointHistory = [];
    context.distanceMatrix = [];
    context.postSimplifyLen = 0;
    context.i = 0;
  }

  // High depth means higher resolution. If our depth is below a certain resolution we will use it as one of our points
  // To get more accurate results raise this resolution cutoff.
  if (point.depth < 10) {
    // Adds newest data to the point history and distance matrix
    context.simplifiedPointHistory.push([point.latitude, point.longitude]);
    addToDistanceMatrix(context.simplifiedPointHistory, context.distanceMatrix);
    context.i++;

    // Every 100 points will be concatinated into a simplified version of itself and added to the net history
    // Increase this 100 number for further accuracy OR this block can be commented out entirely for best accuarcy
    if (context.i % 100 === 0) {
      const oldPoints = context.simplifiedPointHistory.slice(0, context.postSimplifyLen);
      const freshPoints = context.simplifiedPointHistory.slice(
        context.postSimplifyLen,
        context.simplifiedPointHistory.length,
      );
      context.simplifiedPointHistory = oldPoints.concat(
        // Douglas puecker is used to simplify the point history. With a resolution of 1/15th the minimum grid size
        simplify.douglasPeucker(freshPoints, smallestRectLen(freshPoints) / 15, false),
      );
      context.postSimplifyLen = context.simplifiedPointHistory.length;
      context.distanceMatrix = createDistanceMatrix(context.simplifiedPointHistory);
    }

    // Will simplify and merge all the data if the size increases past 300. This second layer ensures the data size is never
    // too big which may highly comprimise speed. 300 is chosen as it provides sufficient accuracy without comprimising speed.
    // Increase 300 to increase accuracy
    if (context.simplifiedPointHistory.length > 300) {
      // Douglas puecker is applied with a finer simplificaiton mesh of 1/100th the minimum grid size to ensure minimal loss
      // of critical data
      context.simplifiedPointHistory = simplify.douglasPeucker(
        context.simplifiedPointHistory,
        smallestRectLen(context.simplifiedPointHistory) / 100,
        false,
      );
      context.distanceMatrix = createDistanceMatrix(context.simplifiedPointHistory);
      context.postSimplifyLen = context.simplifiedPointHistory.length;
    }
  }

  // Only recalculate every 7th incoming point. This is fine as XC distance usually doesnt change often. No accuracy is lost
  // as all points are still in the point history
  if (context.i % 7 !== 0) {
    point.xcDistance = context.prevPoint.xcDistance;
  } else {
    const xdist = maximiseNPointDistance(5, context.distanceMatrix, context.simplifiedPointHistory);
    // console.log(context.distanceMatrix[context.distanceMatrix.length -1])
    // Only adds to the x distance array if its greater then current xdist. Keeps it always increasing
    if (context.prevPoint.xcDistance > xdist) {
      point.xcDistance = context.prevPoint.xcDistance;
    } else {
      point.xcDistance = xdist;
    }
  }
};
